import type {Content} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import React, {memo, useEffect, useRef} from 'react'
import {useLeadIdContext} from '../context/LeadIdContext'
import {urlIncludes} from '../helpers'
import {useProductTourAnalytics} from '../hooks'

type Tour = Content.ProductTourSectionSliceDefaultPrimaryToursItem

type StorylaneDemoMessage = {
  message: string
  payload?: {
    event?: string
    target?: {url?: string}
    demo?: {name?: string}
    chapter?: {name?: string}
    step?: {index?: number}
  }
}

type PendingDemoEvent = {
  chapterStart?: {
    demoName: string
    chapterName: string
    productName: string
    startEvent?: Tour['start_event']
  }
  firstStep?: {
    demoName: string
    stepNumber: number
    chapterName: string
  }
}

interface TourViewerProps {
  tour: Tour | null
  tourStarted: boolean
  children?: React.ReactNode
  loadDemo: boolean
  pageUid: string
}

export const TourViewer = memo<TourViewerProps>(({tour, tourStarted, children, pageUid}) => {
  const {demo_link, product} = tour || {}
  const formattedProductName = product?.toLowerCase().replace(/\s+/g, '-')
  const demoUrl = demo_link && isFilled.link(demo_link) ? demo_link.url : undefined
  const {leadId} = useLeadIdContext()

  const {
    demoStepCounter,
    onDemoLoadError,
    chapterFinished,
    handleDemoEventApi,
    chapterStarted,
    handleConvertCtaClick,
    demoButtonClick,
    getStartedButtonClick,
  } = useProductTourAnalytics(pageUid)

  // Store pending demo events that fire before leadId is available
  const pendingDemoEventsRef = useRef<PendingDemoEvent>({})

  // Add event listener to get demo events
  useEffect(() => {
    const handleDemoEvent = (event: MessageEvent<unknown>) => {
      const data = event.data as StorylaneDemoMessage

      if (data.message === 'storylane-demo-event' && data.payload?.event) {
        switch (data.payload.event) {
          case 'convert_cta': {
            handleConvertCtaClick(data.payload)
            break
          }

          case 'open_external_url': {
            if (urlIncludes(data.payload?.target, 'intent=self_serve')) {
              getStartedButtonClick(formattedProductName || 'Demo')
            }

            if (urlIncludes(data.payload?.target, 'intent=intro')) {
              demoButtonClick(formattedProductName || 'Demo')
            }

            break
          }

          case 'chapter_start': {
            const demoName = data.payload?.demo?.name || `Demo: ${formattedProductName}`
            const chapterName = data.payload?.chapter?.name || 'Chapter'
            const productName = formattedProductName || 'unknown'

            if (leadId) {
              // LeadId is available, send analytics immediately
              chapterStarted(demoName, chapterName, productName)

              if (tour?.start_event) {
                handleDemoEventApi(tour?.start_event)
              }
            } else {
              // LeadId not available yet, store event for later
              pendingDemoEventsRef.current.chapterStart = {
                demoName,
                chapterName,
                productName,
                startEvent: tour?.start_event,
              }
            }

            break
          }

          case 'step_view': {
            const demoName = formattedProductName || 'Demo'
            const stepNumber = data.payload.step?.index || 0
            const chapterName = data.payload?.chapter?.name || 'Chapter'

            if (leadId) {
              // LeadId is available, send analytics immediately
              demoStepCounter(demoName, stepNumber, chapterName)
            } else {
              // LeadId not available yet, store only the first step event
              if (!pendingDemoEventsRef.current.firstStep) {
                pendingDemoEventsRef.current.firstStep = {
                  demoName,
                  stepNumber,
                  chapterName,
                }
              }
            }

            break
          }

          case 'chapter_end': {
            chapterFinished(
              data.payload?.demo?.name || `Demo: ${formattedProductName}`,
              data.payload?.chapter?.name || 'Chapter',
              formattedProductName || 'unknown',
            )

            if (tour?.finish_event) {
              handleDemoEventApi(tour?.finish_event)
            }

            break
          }
        }
      }
    }

    window.addEventListener('message', handleDemoEvent)

    return () => {
      window.removeEventListener('message', handleDemoEvent)
    }
  }, [
    demoStepCounter,
    onDemoLoadError,
    formattedProductName,
    chapterFinished,
    chapterStarted,
    handleDemoEventApi,
    tour,
    handleConvertCtaClick,
    demoButtonClick,
    getStartedButtonClick,
    product,
    leadId,
  ])

  // Send pending demo events when leadId becomes available
  useEffect(() => {
    if (leadId) {
      const pendingEvents = pendingDemoEventsRef.current

      // Send pending chapter_start event
      if (pendingEvents.chapterStart) {
        const {demoName, chapterName, productName, startEvent} = pendingEvents.chapterStart
        chapterStarted(demoName, chapterName, productName)

        if (startEvent) {
          handleDemoEventApi(startEvent)
        }

        // Clear the pending chapter_start event
        pendingEvents.chapterStart = undefined
      }

      // Send pending first step event
      if (pendingEvents.firstStep) {
        const {demoName, stepNumber, chapterName} = pendingEvents.firstStep
        demoStepCounter(demoName, stepNumber, chapterName)

        // Clear the pending first step event
        pendingEvents.firstStep = undefined
      }
    }
  }, [leadId, chapterStarted, handleDemoEventApi, demoStepCounter])

  if (!demoUrl) {
    return (
      <div className="relative aspect-[3/2] h-auto w-full">
        <div className="flex size-full items-center justify-center rounded-lg bg-moss-grey-150" />
      </div>
    )
  }

  return (
    <div className="relative aspect-[3/2] h-auto max-h-[calc(100vh-80px)] w-full bg-moss-grey-150">
      <iframe
        className="size-full rounded-lg border-0"
        src={demoUrl}
        title={`Product tour: ${product || 'Demo'}`}
        loading="lazy"
        onError={() => {
          onDemoLoadError(formattedProductName || 'Demo')
        }}
      />
      {!tourStarted && children && (
        <div className="absolute inset-0 flex size-full items-center justify-center rounded-lg bg-[rgba(0,0,0,0.5)]">
          {children}
        </div>
      )}
    </div>
  )
})
